import React, { useState } from 'react'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import {
  ProviderPickerModal,
  useBuySubscriptionMutation,
  useDoubleSubmitGuard,
  useStartPayment,
  type ProviderPickerSubmit,
} from '@features/payments'
import { useSubscriptionPricesQuery } from '@features/subscription'
import './SubscriptionBlock.css'
import logo from '../../assets/images/logo.svg'

interface SubscriptionBlockProps {
  hasSubscription?: boolean
  buttonText?: string
}

const getBuyErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409) return 'У вас уже есть активная подписка или незавершённый платёж.'
  if (status === 502) return 'Не удалось связаться с провайдером. Попробуйте ещё раз.'
  return 'Не удалось создать платёж. Попробуйте ещё раз.'
}

const SubscriptionBlock: React.FC<SubscriptionBlockProps> = ({
  hasSubscription = false,
  buttonText = 'Хочу в чат',
}) => {
  const [isPickerOpen, setPickerOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const { data: pricesData } = useSubscriptionPricesQuery(isPickerOpen)
  const buyMutation = useBuySubscriptionMutation()
  const guard = useDoubleSubmitGuard()
  const startPayment = useStartPayment()
  const platform = usePlatform()

  if (hasSubscription) {
    return null
  }

  const openPicker = () => {
    setSubmitError(null)
    setPickerOpen(true)
  }
  const closePicker = () => {
    if (buyMutation.isLoading) return
    setSubmitError(null)
    buyMutation.reset()
    setPickerOpen(false)
  }

  const handleSubmit = ({ planId, provider, saveCard }: ProviderPickerSubmit) => {
    // Замок берём до всего остального: по isLoading второй тап проскакивал и
    // создавал второй счёт.
    if (!guard.tryStart()) return
    setSubmitError(null)
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      { subscription_info_id: planId, provider, save_card: saveCard },
      {
        onSuccess: (invoice) => {
          setPickerOpen(false)
          startPayment(invoice, tab)
        },
        onError: (error) => {
          tab.abort()
          setSubmitError(getBuyErrorText(error))
        },
        onSettled: (_invoice, error) => guard.finish(!error),
      }
    )
  }

  return (
    <>
      <div className="subscription-block">
        <div className="subscription-block-text-container">
          <div className="subscription-block-subtitle">
            Больше возможностей с подпиской
          </div>
          <img src={logo} alt="Logo" className="subscription-block-logo" />
          <div className="subscription-block-description">
            Открой доступ ко всем 250+ рецептам и урокам
          </div>
        </div>
        <button className="subscribe-button" onClick={openPicker}>
          {buttonText}
        </button>
      </div>

      {isPickerOpen && (
        <ProviderPickerModal
          plans={pricesData?.subscriptions ?? []}
          override={pricesData?.override}
          onSubmit={handleSubmit}
          onClose={closePicker}
          isSubmitting={buyMutation.isLoading}
          errorText={submitError}
        />
      )}
    </>
  )
}

export default SubscriptionBlock
